import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useNotify, useRefresh, useDataProvider } from "react-admin";

export const SitesEquipmentsImportDialog: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider() as any;

  const [file, setFile] = React.useState<File | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [report, setReport] = React.useState<any | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setReport(null);
    setError(null);
  };

  const onUpload = async () => {
    if (!file) {
      notify("Choisissez un fichier Excel", { type: "warning" });
      return;
    }
    setLoading(true);
    setError(null);
    setReport(null);

    try {
      const { data } = await dataProvider.importSitesEquipments(file);
      // backend renvoie { data: report }, donc data = { summary, errors, ... }
      setReport(data.data);
      notify("Import terminé", { type: "success" });
      refresh();
    } catch (e: any) {
      // ra-data-simple-rest / fetchJson lance HttpError en 4xx/5xx
      const body = e?.body || e?.json;

      console.log(body)
      if (body?.summary) {
        // backend a retourné un report d'erreurs de validation
        setReport(body);
        notify(
          "Erreurs détectées : aucune donnée écrite (vérifiez le rapport).",
          {
            type: "warning",
          }
        );
      } else {
        const msg = e?.message || "Import échoué";
        setError(msg);
        notify(msg, { type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // colonnes DANS L'ORDRE attendu
    const header = [
      "siteName",
      "internalCode",
      "email",
      "siret",
      "address",
      "zipCode",
      "city",
      "contactName",
      "phone",
      "equipname",
      "size",
      "unit",
      "quantity",
      "waste",
      "density",
      "frequency",
      "weekdays",
      "ordinal",
      "monthlyWeekday",
      "startDate",
      "endDate",
      "weekOffset",
      "anchorMonth",
      "exceptions",
      "timezone",
      "notes",
    ].join(",");

    // quelques exemples réalistes
    // Site A avec 2 équipements
    // - bac biodéchet (collecte quotidienne en semaine)
    // - benne DIB (hebdo mardi/jeudi)
    // Site B avec autre équipement
    const exampleLines = [
      [
        "Restaurant Paris A", // siteName
        "CLI-001", // internalCode
        "contact.paris-a@client.com", // email
        "12345678900011", // siret
        "10 rue du Test", // address
        "75001", // zipCode
        "Paris", // city
        "Paul Martin", // contactName
        "0102030405", // phone
        "Bac roulant", // equipname
        "330", // size
        "L", // unit
        "12", // quantity
        "BIODECHET", // waste
        "0.8", // density
        "DAILY", // frequency
        "MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY|SATURDAY", // weekdays
        "FIRST", // ordinal (non utilisé pour DAILY mais requis dans le fichier)
        "MONDAY", // monthlyWeekday
        "2025-01-01", // startDate
        "2025-12-31", // endDate
        "0", // weekOffset
        "1", // anchorMonth
        "2025-05-01|2025-12-25", // exceptions
        "Europe/Paris", // timezone
        "Collecte quotidienne en cuisine", // notes
      ].join(","),

      [
        "Restaurant Paris A", // même site
        "CLI-001",
        "contact.paris-a@client.com",
        "12345678900011",
        "10 rue du Test",
        "75001",
        "Paris",
        "Paul Martin",
        "0102030405",
        "Benne DIB",
        "10",
        "m3",
        "2",
        "DIB",
        "0.3",
        "WEEKLY",
        "TUESDAY|THURSDAY",
        "FIRST",
        "MONDAY",
        "2025-01-01",
        "2025-12-31",
        "0",
        "1",
        "",
        "Europe/Paris",
        "Ramassage 2x/semaine DIB",
      ].join(","),

      [
        "Usine Lyon B",
        "CLI-002",
        "env.lyon-b@client.com",
        "22345678900011",
        "20 avenue Y",
        "69001",
        "Lyon",
        "Sarah Dupont",
        "0102030406",
        "Compacteur carton",
        "20",
        "m3",
        "1",
        "CARTON",
        "0.2",
        "MONTHLY",
        "", // weekdays (pas utilisé mensuel pur)
        "FIRST", // ordinal: FIRST lundi du mois
        "MONDAY", // monthlyWeekday
        "2025-01-01",
        "2025-12-31",
        "0", // weekOffset
        "1", // anchorMonth
        "", // exceptions
        "Europe/Paris",
        "Passage mensuel compacteur carton",
      ].join(","),
    ];

    const csv = header + "\n" + exampleLines.join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "trackency_import_sites_equipments.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Importer Sites &amp; Équipements (Excel/CSV)</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            <Typography variant="body2" component="div">
              <b>Principe :</b>
              <br />• Chaque <b>ligne</b> = informations du site + UN équipement
              + son plan de collecte.
              <br />• Le même site peut apparaître sur plusieurs lignes (même{" "}
              <code>internalCode</code>), il sera mis à jour une seule fois.
              <br />• Les équipements sont upsertés par combinaison{" "}
              <i>(site, name, size, unit)</i>.
              <br />• Les plans de collecte (<code>frequency</code>,
              <code>weekdays</code>, <code>ordinal</code>, etc.) sont créés /
              mis à jour avec l'équipement.
              <br />• Si une erreur de validation est détectée sur une seule
              ligne,
              <b>rien n'est importé</b>.
            </Typography>
          </Alert>

          <Stack direction="row" spacing={2} alignItems="center">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <Button onClick={downloadTemplate} size="small">
              Télécharger le modèle
            </Button>
          </Stack>

          {loading && <LinearProgress />}

          {error && <Alert severity="error">{error}</Alert>}

          {report && (
            <Alert
              severity={report?.summary?.errors > 0 ? "warning" : "success"}
            >
              <div>
                <div>
                  <b>Sites</b> — créés: {report.summary.sitesCreated} / mis à
                  jour: {report.summary.sitesUpdated}
                </div>
                <div>
                  <b>Équipements</b> — créés: {report.summary.equipmentsCreated}{" "}
                  / mis à jour: {report.summary.equipmentsUpdated}
                </div>
                <div>
                  <b>Erreurs</b>: {report.summary.errors} /{" "}
                  <b>Avertissements</b>: {report.summary.warnings}
                </div>

                {report.errors?.length ? (
                  <>
                    <br />
                    <b>Erreurs détectées :</b>
                    <ul>
                      {report.errors.slice(0, 20).map((e: any, i: number) => (
                        <li key={i}>
                          Ligne {e.row}: {e.message}
                        </li>
                      ))}
                    </ul>
                    {report.errors.length > 20 && (
                      <Typography variant="caption">
                        …et {report.errors.length - 20} autres
                      </Typography>
                    )}
                  </>
                ) : null}
              </div>
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" component="div">
            <b>Colonnes attendues (dans cet ordre) :</b>
            <br />
            siteName, internalCode, email, siret, address, zipCode, city,
            contactName, phone,
            <br />
            equipname, size, unit, quantity, waste, density,
            <br />
            frequency, weekdays, ordinal, monthlyWeekday,
            <br />
            startDate, endDate, weekOffset, anchorMonth, exceptions, timezone,
            notes.
            <br />
            <br />
            <b>frequency</b> = DAILY / WEEKLY / BIWEEKLY / MONTHLY / QUARTERLY /
            ANNUALLY
            <br />
            <b>weekdays</b> = liste des jours séparés par "|" (ex:
            MONDAY|TUESDAY)
            <br />
            <b>ordinal</b> = FIRST / SECOND / THIRD / FOURTH / LAST / ALL
            <br />
            <b>monthlyWeekday</b> = MONDAY ... SUNDAY
            <br />
            <b>exceptions</b> = dates exclues séparées par "|" (ex:
            2025-05-01|2025-12-25)
            <br />
            <b>timezone</b> = Europe/Paris
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Fermer
        </Button>
        <Button
          variant="contained"
          onClick={onUpload}
          disabled={!file || loading}
        >
          Importer
        </Button>
      </DialogActions>
    </Dialog>
  );
};
